// ─── Shared utilities for the Custom JD flow ─────────────────────────────────

// ── Experience level helpers ──────────────────────────────────────────────────

/**
 * Convert "0-1" / "15+" experience-level string → { minExp, maxExp } numbers.
 * "15+" (and any "N+" pattern) maps maxExp to 99 (unbounded).
 */
export function experienceLevelToMinMax(level) {
  if (!level) return { minExp: 0, maxExp: 1 };
  if (level.includes("+")) {
    const min = parseInt(level.replace("+", ""), 10);
    return { minExp: isNaN(min) ? 0 : min, maxExp: 99 };
  }
  const [min, max] = level.split("-").map((v) => parseInt(v, 10));
  return { minExp: isNaN(min) ? 0 : min, maxExp: isNaN(max) ? 1 : max };
}

/** Convert { minExp, maxExp } back → experience-level string ("0-1" / "15+"). */
export const minMaxToExperienceLevel = (minExp, maxExp) =>
  maxExp >= 99 ? `${minExp}+` : `${minExp}-${maxExp}`;

// ── Topic-count helpers ───────────────────────────────────────────────────────

/** Minutes of interview time allocated per topic. */
export const MINS_PER_TOPIC = 3;

/** Minimum topics guaranteed per skill regardless of weightage. */
export const MIN_TOPICS_PER_SKILL = 2;

/**
 * Maximum topics to ask for a skill given total round duration and its weightage.
 * Formula: max(MIN_TOPICS_PER_SKILL, floor(duration / MINS_PER_TOPIC * weightage%))
 */
export function calcMaxTopics(duration, weightage) {
  return Math.max(MIN_TOPICS_PER_SKILL, Math.floor((duration / MINS_PER_TOPIC) * (weightage / 100)));
}

// ── Weightage helpers ─────────────────────────────────────────────────────────

/**
 * Max weightage a single skill can receive so every other skill keeps at least 10%.
 * Formula: 100 - (skillCount - 1) * 10
 */
export function calcMaxWeightage(skillCount) {
  return 100 - (skillCount - 1) * 10;
}

/**
 * Equal-distribution weightages totalling exactly 100.
 * If existingWeightages has values, returns those unchanged (edit JD path).
 * e.g. 3 skills → 34, 33, 33
 */
export function computeDefaultWeightages(skills, existingWeightages) {
  if (Object.keys(existingWeightages).length > 0)
    return skills.reduce((acc, s) => ({ ...acc, [s]: existingWeightages[s] || 0 }), {});
  const base = Math.floor(100 / skills.length);
  const extra = 100 - base * skills.length;
  return skills.reduce((acc, s, i) => ({ ...acc, [s]: base + (i < extra ? 1 : 0) }), {});
}

// ── Focus area helpers ────────────────────────────────────────────────────────

/** Max focus areas a user can select: 1 area per 10 mins, capped at 8 (matches generated count). */
export function calcMaxSelectableFocusAreas(duration) {
  return Math.min(Math.max(1, Math.floor(duration / 10)), 8);
}

/**
 * How many focus areas the backend generates.
 * Formula: selectable_max + 3 buffer, capped at 8.
 * Regen always returns 8 (user is browsing for replacements).
 * Must stay in sync with focus_areas.py → _focus_area_count().
 */
export function calcGenerateFocusAreaCount(duration, isRegen = false) {
  if (isRegen) return 8;
  return Math.min(Math.floor(duration / 10) + 3, 8);
}

